import { CELL_GAP, DRAFT_CARD_HEIGHT, GRID_GAP, ROW_HEADER_WIDTH } from './constants';
import {
  getGridOriginX,
  getGridOriginY,
  getGroupColumnOffset,
  getStuckColumnHeaderBottom,
  type LeadingColumns,
} from './layoutMetrics';

const DRAFT_SLOT_HEIGHT = () => DRAFT_CARD_HEIGHT.value + CELL_GAP.value;

export const getCardSlotHeight = ({
  cardHeight,
  hasCellDraftAbove,
  hasCardDraftBelow,
}: {
  cardHeight: number;
  hasCellDraftAbove: boolean;
  hasCardDraftBelow: boolean;
}): number =>
  (hasCellDraftAbove ? DRAFT_SLOT_HEIGHT() : 0) +
  cardHeight +
  (hasCardDraftBelow ? DRAFT_SLOT_HEIGHT() : 0);

type CardSlot = { cardHeight: number; hasCardDraftBelow: boolean };

export type Extent = { start: number; end: number };

/** A card's vertical extent in row-virtualizer coordinates. */
export const getCardExtent = ({
  rowStart,
  slots,
  cardIndex,
  hasCellDraft,
}: {
  rowStart: number;
  slots: readonly CardSlot[];
  cardIndex: number;
  hasCellDraft: boolean;
}): Extent => {
  const cardsAbove = slots.slice(0, cardIndex).reduce((sum, slot) => {
    const draftBelow = slot.hasCardDraftBelow ? DRAFT_SLOT_HEIGHT() : 0;
    return sum + slot.cardHeight + draftBelow + CELL_GAP.value;
  }, 0);
  const start = rowStart + (hasCellDraft ? DRAFT_SLOT_HEIGHT() : 0) + cardsAbove;
  return { start, end: start + (slots[cardIndex]?.cardHeight ?? 0) };
};

/** Virtualizer offsets exclude the header blocks; sticky ones also cover the viewport start. */
export type Axis = { viewportSize: number; gridStart: number; coveredStart: number };

export const getVerticalAxis = ({
  viewportHeight,
  hasAxisLabelColumn,
  isHeadersSticky,
}: {
  viewportHeight: number;
  hasAxisLabelColumn: boolean;
  isHeadersSticky: boolean;
}): Axis => {
  return {
    viewportSize: viewportHeight,
    gridStart: getGridOriginY(hasAxisLabelColumn),
    coveredStart: isHeadersSticky ? getStuckColumnHeaderBottom(hasAxisLabelColumn) : 0,
  };
};

/** Right edge of the stuck columns. The axis column's border also covers the gap after it. */
export const getStickyCoverX = (columns: LeadingColumns): number => {
  const origin = getGridOriginX(columns);
  if (origin === 0) return 0;
  return columns.hasRowHeaders ? origin - GRID_GAP.value : origin;
};

export const getHorizontalAxis = ({
  viewportWidth,
  gridOriginX,
  stickyCoverX,
  isHeadersSticky,
}: {
  viewportWidth: number;
  gridOriginX: number;
  stickyCoverX: number;
  isHeadersSticky: boolean;
}): Axis => ({
  viewportSize: viewportWidth,
  gridStart: gridOriginX,
  coveredStart: isHeadersSticky ? stickyCoverX : 0,
});

export const getVisibleBand = (
  { viewportSize, gridStart, coveredStart }: Axis,
  scrollOffset: number,
): Extent => ({
  start: scrollOffset + coveredStart - gridStart,
  end: scrollOffset + viewportSize - gridStart,
});

export const getVisibleFraction = (band: Extent, extent: Extent): number => {
  const size = extent.end - extent.start;
  if (size <= 0) return extent.start >= band.start && extent.start <= band.end ? 1 : 0;
  const overlap = Math.min(extent.end, band.end) - Math.max(extent.start, band.start);
  return Math.max(0, overlap) / size;
};

export const getScrollToCenter = (
  { viewportSize, gridStart, coveredStart }: Axis,
  extent: Extent,
): number => {
  const center = (extent.start + extent.end) / 2;
  return Math.max(0, center + gridStart - coveredStart - (viewportSize - coveredStart) / 2);
};

type RowHeaderBlockLayout = {
  hasRowHeaders: boolean;
  hasGroupHeaders: boolean;
  hasAxisLabelRow: boolean;
  isAddRowHeaderButtonVisible: boolean;
  rowHeaderOrientation: 'vertical' | 'horizontal';
  rowHeadersWidth: number;
};

/** Uses the vertical row-header width, not `AXIS_HEIGHT`, to match the rendered corner. */
export const getRowHeaderBlockWidth = ({
  hasRowHeaders,
  hasGroupHeaders,
  hasAxisLabelRow,
  isAddRowHeaderButtonVisible,
  rowHeaderOrientation,
  rowHeadersWidth,
}: RowHeaderBlockLayout): number => {
  const groupColumnExtra = getGroupColumnOffset(hasGroupHeaders);
  const axisWidth = hasAxisLabelRow ? ROW_HEADER_WIDTH.vertical.value + GRID_GAP.value : 0;
  const baseHeaderWidth = hasRowHeaders
    ? rowHeaderOrientation === 'vertical'
      ? ROW_HEADER_WIDTH.vertical.value
      : rowHeadersWidth
    : isAddRowHeaderButtonVisible
      ? ROW_HEADER_WIDTH.vertical.value
      : 0;
  return baseHeaderWidth > 0 || axisWidth > 0
    ? groupColumnExtra + baseHeaderWidth + axisWidth
    : axisWidth;
};
